import React from 'react';
import { Alert, AlertDescription, AlertIcon, AlertTitle, Box, Checkbox, Text } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import colors from '../../styles/theme/foundation/colors';

const pluralRecords = count => (count === 1 ? '1 record' : `${count} records`);

const PAYMENT = 'which may change whether Procore Pay holds payment.';

export const procoreWarningMessage = (scope, affectedRecords = null) => {
  switch (scope) {
    case 'record':
      return 'This vendor\'s compliance is written to Procore. Archiving or deleting this record updates or '
        + `removes their "PINS Record" insurance row on the Procore project, ${PAYMENT}`;
    case 'job':
      return 'Compliance for this project\'s vendors is written to Procore. Archiving or deleting it removes '
        + `their "PINS Record" insurance rows from the Procore project, ${PAYMENT}`;
    case 'department':
      // Counts records, not rows: several records can feed one vendor's row,
      // so the copy does not promise a row per record.
      return Number.isInteger(affectedRecords)
        ? `${pluralRecords(affectedRecords)} in this department feed "PINS Record" insurance rows in Procore. `
          + `Those rows will be removed or recalculated, ${PAYMENT}`
        : `Records in this department feed "PINS Record" insurance rows in Procore. Those rows will be removed or recalculated, ${PAYMENT}`;
    case 'insured':
      return `This vendor's "PINS Record" insurance rows will be removed from every Procore project they are on, ${PAYMENT}`;
    default:
      return `"PINS Record" insurance rows will be removed from Procore, ${PAYMENT}`;
  }
};

/**
 * The Procore warning shown inside archive and delete confirmations.
 *
 * Only the record-level warning can be dismissed, and only by the person
 * seeing it; pass `onDismissChange` to offer that.
 */
const ProcoreWritebackWarning = ({ scope, affectedRecords, onDismissChange }) => (
  <>
    <Alert
      alignItems="flex-start"
      backgroundColor={colors.caution.primary['100']}
      borderRadius="md"
      data-testid="procore-writeback-warning"
      flexDirection="column"
      status="warning"
      textAlign="left"
    >
      <Box alignItems="center" display="flex">
        <AlertIcon />
        <AlertTitle color={colors.greyScale['900']}>Procore will be updated</AlertTitle>
      </Box>
      <AlertDescription mt={1}>{procoreWarningMessage(scope, affectedRecords)}</AlertDescription>
    </Alert>
    {onDismissChange && (
    <Checkbox mt={3} onChange={e => onDismissChange(e.target.checked)}>
      <Text textStyle="bodyMd500">Don&apos;t show me this Procore warning again</Text>
    </Checkbox>
    )}
  </>
);

ProcoreWritebackWarning.propTypes = {
  /** What is being archived or deleted */
  scope: PropTypes.oneOf(['record', 'job', 'department', 'insured']).isRequired,
  /** Department only: records whose rows will be removed, or null if unknown */
  affectedRecords: PropTypes.number,
  /** Record only: receives the "don't show again" checkbox state */
  onDismissChange: PropTypes.func
};

ProcoreWritebackWarning.defaultProps = {
  affectedRecords: null,
  onDismissChange: undefined
};

export default ProcoreWritebackWarning;
